import serviceToken from '../../services/requestToken';
import serviceTokenNew from '../../services/requestTokenNew';

const RETAILER_API_URL = process.env.REACT_APP_RETAILER_BASE_API;

/**
 * @description Fetch active public subscription plans, each with its public price options.
 * @returns {Promise} resolves to `{ success, message, data }` (see `serviceTokenNew`'s response interceptor) where `data` is an array of plans, each `{ id, name, description, type, is_active, prices: [{ id, plan_id, display_name, billing_interval, interval_count, amount, currency, is_active, ... }] }` (wire shape is snake_case)
 */
export const getSubscriptionPlansApi = () => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/plans`,
    method: 'get'
  });
};

/**
 * @description Fetch the retailer's current subscription (any status) and its plan detail, if one exists.
 * @returns {Promise} resolves to `{ success, message, data }` where `data` is `{ subscription, plan }` (both nullable, wire shape is snake_case — see `useSubscriptionStore`'s `SubscriptionApi`/`SubscriptionPlanApi` types)
 */
export const getCurrentSubscriptionApi = () => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/current`,
    method: 'get'
  });
};

/**
 * @description Fetch the retailer's masked card-on-file for subscription billing.
 * @returns {Promise} resolves to `{ success, message, data }` where `data` is `{ card_holder_name, card_last4, card_brand, card_expiry_month, card_expiry_year }`, or `null` if no verified card is on file (wire shape is snake_case)
 */
export const getPaymentMethodApi = () => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/payment-method`,
    method: 'get'
  });
};

/**
 * @description Fetch the retailer's subscription billing history — paid invoices only (the current in-progress period's 'open' invoice and any 'void' invoices are excluded), newest first.
 * @returns {Promise} resolves to `{ success, message, data }` where `data` is an array of invoices, each `{ id, subscription_id, status, invoice_number, plan_display_name, billing_period_start, billing_period_end, subtotal, tax_rate, tax_amount, total, currency, pdf_url, issued_at, ... }` (wire shape is snake_case)
 */
export const getInvoicesApi = () => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/invoices`,
    method: 'get'
  });
};

/**
 * @description Download an invoice's PDF, streamed through the backend (the underlying Drive file is private). Uses `serviceToken`, not `serviceTokenNew` — its response interceptor returns the raw axios response instead of unwrapping a `{ success, data }` JSON envelope, which a binary PDF response doesn't have.
 * @param {string} invoiceId
 * @returns {Promise} resolves to the axios response, with `response.data` as a PDF `Blob`
 */
export const downloadInvoicePdfApi = (invoiceId) => {
  return serviceToken.get(`${RETAILER_API_URL}subscription/invoices/${invoiceId}/pdf`, {
    responseType: 'blob'
  });
};

/**
 * @description Initialise a zero-amount Airwallex payment intent used to verify the retailer's card, and check this retailer's trial eligibility for the given plan (drives the pre-auth modal's trial-vs-charge copy).
 * @param {Object} data
 * @param {string} data.planId - plan the retailer is about to verify a card for
 * @param {string} data.planPriceId - plan price the retailer is about to verify a card for
 * @returns {Promise} resolves to `{ success, message, data }` where `data` is `{ payment_intent: { payment_intent_id, client_secret, currency }, payment_consent: { next_triggered_by, merchant_trigger_reason }, trial_eligible }` (wire shape is snake_case)
 */
export const initCardVerificationApi = (data) => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/card-verification/init`,
    method: 'post',
    data
  });
};

/**
 * @description Persist the verified card's payment consent/method and display details on the retailer's subscription profile, then either start a trial or charge the card immediately, depending on the plan's trial_days and the retailer's trial history.
 * @param {Object} data
 * @param {string} data.paymentIntentId - Airwallex payment intent ID the client confirmed directly with Airwallex, re-verified server-side
 * @param {string} data.awxPaymentConsentId - Airwallex consent ID authorising future recurring charges
 * @param {string} data.awxPaymentMethodId - Airwallex payment method ID
 * @param {string} data.cardHolderName
 * @param {string} data.cardLast4
 * @param {string} data.cardBrand
 * @param {number} data.cardExpiryMonth
 * @param {number} data.cardExpiryYear
 * @param {string} data.planId - plan to subscribe to
 * @param {string} data.planPriceId - plan price to subscribe to
 * @returns {Promise} resolves to `{ success, message, data }` where `data` is `{ profile, subscription, outcome: 'trial' | 'charged' }` on success (wire shape is snake_case). On a declined immediate charge, rejects with a 402 response whose body is `{ declineReason }` directly (camelCase — not wrapped in the usual `{ success, message, data }` envelope, so it isn't snake-cased either).
 */
export const completeCardVerificationApi = (data) => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/card-verification/complete`,
    method: 'post',
    data
  });
};

/**
 * @description Initialise a zero-amount Airwallex payment intent to verify a replacement card for the retailer's already-active subscription. Unlike `initCardVerificationApi`, this doesn't take a plan and doesn't check trial eligibility — it requires an active subscription rather than the absence of one.
 * @returns {Promise} resolves to `{ success, message, data }` where `data` is `{ payment_intent: { payment_intent_id, client_secret, currency }, payment_consent: { next_triggered_by, merchant_trigger_reason } }` (wire shape is snake_case)
 */
export const initPaymentMethodApi = () => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/payment-method/init`,
    method: 'post'
  });
};

/**
 * @description Replace the card on file for the retailer's active subscription, using an already-verified Airwallex card. Does not create or affect a subscription — see `completeCardVerificationApi` for trial signup. Requires an active subscription; not available once a subscription has lapsed (`exhausted`).
 * @param {Object} data
 * @param {string} data.paymentIntentId - Airwallex payment intent ID the client confirmed directly with Airwallex, re-verified server-side
 * @param {string} data.awxPaymentConsentId - Airwallex consent ID authorising future recurring charges
 * @param {string} data.awxPaymentMethodId - Airwallex payment method ID
 * @param {string} data.cardHolderName
 * @param {string} data.cardLast4
 * @param {string} data.cardBrand
 * @param {number} data.cardExpiryMonth
 * @param {number} data.cardExpiryYear
 * @returns {Promise} resolves to `{ success, message, data }` where `data` is `{ card_holder_name, card_last4, card_brand, card_expiry_month, card_expiry_year }` (wire shape is snake_case)
 */
export const updatePaymentMethodApi = (data) => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/payment-method`,
    method: 'post',
    data
  });
};

/**
 * @description Cancel the retailer's active subscription. Access is retained until the current billing period ends (or immediately, if still in trial).
 * @param {string} [reason] - optional free-text reason recorded on the audit log
 * @returns {Promise} resolves to `{ success, message, data }` where `data` is the updated subscription (wire shape is snake_case — see `SubscriptionApi`)
 */
export const cancelSubscriptionApi = (reason) => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/cancel`,
    method: 'post',
    data: { reason }
  });
};

/**
 * @description Schedule a plan/price change on the retailer's active subscription. No charge happens now — the swap is applied by the renewal cron at the next `current_period_end`, replacing any previously scheduled change. Not available while still in trial.
 * @param {Object} data
 * @param {string} data.planId - target plan to switch to
 * @param {string} data.planPriceId - target plan price to switch to
 * @returns {Promise} resolves to `{ success, message, data }` where `data` is the updated subscription, with `pending_plan_id`/`pending_plan_price_id` set (wire shape is snake_case — see `SubscriptionApi`)
 */
export const changePlanApi = (data) => {
  return serviceTokenNew({
    url: `${RETAILER_API_URL}subscription/change-plan`,
    method: 'post',
    data
  });
};
